import React, {FC} from 'react';
import {IProduct, IProductOptionSelection} from '../../../../../types/galleryTypes';
import {ColorPicker} from '@wix/wix-ui-tpa/cssVars';
import {RadioButtonChangeEvent, RadioButtonClickEvent} from 'wix-ui-core';
import {IProductSelectionAvailabilityMap} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import s from './ProductColorOption.scss';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import {classes as colorOptionStylesNew, style as styleColorOptionNew} from './ColorOption.st.css';
import {classes as colorOptionStylesOld, style as styleColorOptionOld} from './ColorOptionOld.st.css';
import classNames from 'classnames';
import {withGlobals} from '../../../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../../../gallery/galleryGlobalStrategy';
import {Experiments} from '../../../../../constants';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {ConditionalRender} from '../../../ConditionalRender/ConditionalRender';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/src/constants';
import {ProductOptionsChangeData, DataHook} from '../ProductOptions';
import {useOptionSelectionLogic} from '../../hooks/options/useOptionSelectionLogic';

interface IProductColorOptionProps extends IGalleryGlobalProps {
  selectionIds: number[];
  onSelectionIdsChange(
    optionSelections: IProductColorOptionProps['selectionIds'],
    changeData: ProductOptionsChangeData
  ): Promise<void>;
  option: IProduct['options'][number];
  selectionsAvailability: IProductSelectionAvailabilityMap[string];
  url: string;
  isLoading: boolean;
}

const ColorOption: FC<IProductColorOptionProps & IProvidedTranslationProps> = (props) => {
  const {
    selectionsAvailability,
    t,
    url,
    selectionIds,
    onSelectionIdsChange,
    option,
    globals: {useExperiments, isRTL, stylesParams, styles, isLiveSiteMode, isPreviewMode},
    isLoading,
  } = props;

  const productOptionsLimitEnabled = useExperiments.enabled(Experiments.GalleryProductOptionsLimit);
  const shouldSeparateQuantityAndOptionsStyleParams = useExperiments.enabled(
    Experiments.ShouldSeparateQuantityAndOptionsStyleParams
  );
  const isColorOptionAlignmentEnabled = useExperiments.enabled(Experiments.GalleryColorOptionAlignment);

  const colorOptionStyles = isColorOptionAlignmentEnabled ? colorOptionStylesNew : colorOptionStylesOld;
  const styleColorOption = isColorOptionAlignmentEnabled ? styleColorOptionNew : styleColorOptionOld;

  const {visibleSelections, calculateNextSelectionIds, isSelectionDisabled, isSelectionChecked} =
    useOptionSelectionLogic({
      option,
      selectionIds,
      selectionsAvailability,
    });

  const mapSelectionToColorPickerItem = (selection: IProductOptionSelection, additionalClass: string) => {
    const isDisabled = isSelectionDisabled(selection.id);
    return (
      <ColorPicker.Item
        key={selection.id}
        className={classNames(s.colorPickerItem, additionalClass)}
        aria-label={selection.description}
        value={selection.value}
        disabled={isDisabled}
        isCrossedOut={isDisabled}
        checked={isSelectionChecked(selection.id)}
      />
    );
  };

  const getColorPickerItems = () => {
    const generateColorPickerItems = (selections: IProductOptionSelection[], additionalClass = '') =>
      selections.map((selection) => mapSelectionToColorPickerItem(selection, additionalClass));

    if (!productOptionsLimitEnabled) {
      return generateColorPickerItems(visibleSelections);
    }

    const productOptionSwatchesLimit = styles.get(stylesParams.gallery_productOptionSwatchesLimit);
    const shownSelections = visibleSelections.slice(0, productOptionSwatchesLimit);
    const remainingSelections = visibleSelections.slice(productOptionSwatchesLimit);

    return [
      ...generateColorPickerItems(shownSelections),
      ...generateColorPickerItems(remainingSelections, s.colorPickerItemRemaining),
    ];
  };

  const getRemainingColorsCounter = () => {
    if (!productOptionsLimitEnabled) {
      return 0;
    }
    const productOptionSwatchesLimit = styles.get(stylesParams.gallery_productOptionSwatchesLimit);
    return visibleSelections.length - productOptionSwatchesLimit;
  };

  const getRemainingColorsCounterSuffix = () => {
    const remainingColorsCounter = getRemainingColorsCounter();
    const showRemainingColorsCounterSuffix = productOptionsLimitEnabled && remainingColorsCounter > 0;
    if (!showRemainingColorsCounterSuffix) {
      return null;
    }

    const FIRST_COLOR_PICKER_URL_FRAGMENT = `${VIEW_MORE_OPTIONS_URL_FRAGMENT}-1`;
    const urlObj = new URL(url);
    urlObj.hash = FIRST_COLOR_PICKER_URL_FRAGMENT;

    return (
      <ConditionalRender by={'showColorPickerLimit'}>
        <a
          dir="ltr"
          href={isLiveSiteMode || isPreviewMode ? urlObj.href : null}
          data-hook={DataHook.ColorOptionMore}
          className={classNames(s.remainingColorsCounter, {
            [s.separateStyles]: shouldSeparateQuantityAndOptionsStyleParams,
          })}
          aria-label={t('colorSwatchNumMoreOptionsAriaLabel', {numRemainingColors: remainingColorsCounter})}>
          {t('colorSwatchNumChoicesLeftLabel', {numChoiceLeft: remainingColorsCounter})}
        </a>
      </ConditionalRender>
    );
  };

  const sealColorChangeHandler = (event: RadioButtonChangeEvent | RadioButtonClickEvent) => {
    const pickedSelectionId = visibleSelections.find(({value}) => value === event.value)?.id;
    const nextSelectionIds = calculateNextSelectionIds(pickedSelectionId);

    void onSelectionIdsChange(nextSelectionIds, {
      optionType: ProductOptionType.COLOR,
    });
  };

  return (
    <div
      key={option.id}
      data-hook={DataHook.ProductOption}
      dir={isRTL ? 'rtl' : 'ltr'}
      className={classNames({
        separateStyles: shouldSeparateQuantityAndOptionsStyleParams,
      })}>
      <ColorPicker
        className={classNames(
          styleColorOption(colorOptionStyles.colorOption, {
            separateStyles: shouldSeparateQuantityAndOptionsStyleParams,
          })
        )}
        onChange={sealColorChangeHandler}
        data-hook={DataHook.ColorOption}
        aria-label={option.title}
        suffix={getRemainingColorsCounterSuffix()}
        isLoading={isLoading}>
        {getColorPickerItems()}
      </ColorPicker>
    </div>
  );
};

export const ProductColorOption = withGlobals(withTranslations()(ColorOption));
